import { useState } from 'react';
import {
  Stack,
  SimpleGrid,
  Button,
  Modal,
  TextInput,
  Checkbox,
  Textarea,
  Skeleton,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { IconPlus, IconMapPin } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import PageHeader from '@/components/PageHeader';
import AddressCard from '@/components/AddressCard';
import EmptyState from '@/components/EmptyState';
import { useAddresses, useCreateAddress, useUpdateAddress, useDeleteAddress } from '@/hooks/usePortalApi';
import type { Address } from '@/types';

export default function Addresses() {
  const { data: addresses, isLoading } = useAddresses();
  const createAddress = useCreateAddress();
  const updateAddress = useUpdateAddress();
  const deleteAddress = useDeleteAddress();

  const [createOpened, { open: openCreate, close: closeCreate }] = useDisclosure(false);
  const [editOpened, { open: openEdit, close: closeEdit }] = useDisclosure(false);
  const [editAddress, setEditAddress] = useState<Address | null>(null);

  const createForm = useForm({
    initialValues: {
      label: 'Delivery',
      street: '',
      city: '',
      state: '',
      zip_code: '',
      is_default: false,
      delivery_instructions: '',
    },
    validate: {
      street: (v) => (v.trim() ? null : 'Street is required'),
      city: (v) => (v.trim() ? null : 'City is required'),
      state: (v) => (v.trim() ? null : 'State is required'),
      zip_code: (v) => (v.trim() ? null : 'ZIP code is required'),
    },
  });

  const editForm = useForm({
    initialValues: {
      label: '',
      street: '',
      city: '',
      state: '',
      zip_code: '',
      is_default: false,
      delivery_instructions: '',
    },
    validate: {
      street: (v) => (v.trim() ? null : 'Street is required'),
      city: (v) => (v.trim() ? null : 'City is required'),
      state: (v) => (v.trim() ? null : 'State is required'),
      zip_code: (v) => (v.trim() ? null : 'ZIP code is required'),
    },
  });

  const handleCreate = (values: typeof createForm.values) => {
    createAddress.mutate(values, {
      onSuccess: () => {
        notifications.show({ title: 'Address added', message: 'New address has been saved', color: 'brand' });
        closeCreate();
        createForm.reset();
      },
      onError: () => {
        notifications.show({ title: 'Error', message: 'Failed to add address', color: 'red' });
      },
    });
  };

  const handleEdit = (address: Address) => {
    setEditAddress(address);
    editForm.setValues({
      label: address.label,
      street: address.street,
      city: address.city,
      state: address.state,
      zip_code: address.zip_code,
      is_default: address.is_default,
      delivery_instructions: address.delivery_instructions ?? '',
    });
    openEdit();
  };

  const handleEditSubmit = (values: typeof editForm.values) => {
    if (!editAddress) return;
    updateAddress.mutate(
      { id: editAddress.id, ...values },
      {
        onSuccess: () => {
          notifications.show({ title: 'Address updated', message: 'Address has been saved', color: 'brand' });
          closeEdit();
          setEditAddress(null);
          editForm.reset();
        },
        onError: () => {
          notifications.show({ title: 'Error', message: 'Failed to update address', color: 'red' });
        },
      },
    );
  };

  const handleDelete = (address: Address) => {
    if (!window.confirm(`Delete the "${address.label}" address?`)) return;
    deleteAddress.mutate(address.id, {
      onSuccess: () => {
        notifications.show({ title: 'Address deleted', message: 'Address has been removed', color: 'brand' });
      },
      onError: () => {
        notifications.show({ title: 'Error', message: 'Failed to delete address', color: 'red' });
      },
    });
  };

  return (
    <Stack gap="lg">
      <PageHeader
        title="Delivery Addresses"
        description="Manage your delivery locations"
        actions={
          <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
            Add Address
          </Button>
        }
      />

      {isLoading ? (
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          {[...Array(2)].map((_, i) => (
            <Skeleton key={i} height={120} radius="md" />
          ))}
        </SimpleGrid>
      ) : !addresses?.length ? (
        <EmptyState
          icon={<IconMapPin size={30} />}
          title="No addresses"
          description="Add a delivery address for your orders"
          action={
            <Button onClick={openCreate}>
              Add Address
            </Button>
          }
        />
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          {addresses.map((addr) => (
            <AddressCard
              key={addr.id}
              address={addr}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ))}
        </SimpleGrid>
      )}

      {/* Add Address Modal */}
      <Modal opened={createOpened} onClose={closeCreate} title="Add Delivery Address" centered>
        <form onSubmit={createForm.onSubmit(handleCreate)}>
          <Stack gap="sm">
            <TextInput label="Label" placeholder="e.g. Main Warehouse" {...createForm.getInputProps('label')} />
            <TextInput label="Street" required {...createForm.getInputProps('street')} />
            <TextInput label="City" required {...createForm.getInputProps('city')} />
            <TextInput label="State" required {...createForm.getInputProps('state')} />
            <TextInput label="ZIP Code" required {...createForm.getInputProps('zip_code')} />
            <Textarea
              label="Delivery Instructions"
              placeholder="Gate code, dock number, etc."
              {...createForm.getInputProps('delivery_instructions')}
            />
            <Checkbox
              label="Set as default delivery address"
              {...createForm.getInputProps('is_default', { type: 'checkbox' })}
            />
            <Button type="submit" fullWidth loading={createAddress.isPending}>
              Save Address
            </Button>
          </Stack>
        </form>
      </Modal>

      {/* Edit Address Modal */}
      <Modal opened={editOpened} onClose={closeEdit} title="Edit Delivery Address" centered>
        <form onSubmit={editForm.onSubmit(handleEditSubmit)}>
          <Stack gap="sm">
            <TextInput label="Label" placeholder="e.g. Main Warehouse" {...editForm.getInputProps('label')} />
            <TextInput label="Street" required {...editForm.getInputProps('street')} />
            <TextInput label="City" required {...editForm.getInputProps('city')} />
            <TextInput label="State" required {...editForm.getInputProps('state')} />
            <TextInput label="ZIP Code" required {...editForm.getInputProps('zip_code')} />
            <Textarea
              label="Delivery Instructions"
              placeholder="Gate code, dock number, etc."
              {...editForm.getInputProps('delivery_instructions')}
            />
            <Checkbox
              label="Set as default delivery address"
              {...editForm.getInputProps('is_default', { type: 'checkbox' })}
            />
            <Button type="submit" fullWidth loading={updateAddress.isPending}>
              Update Address
            </Button>
          </Stack>
        </form>
      </Modal>
    </Stack>
  );
}
